import gql from 'graphql-tag';

// Every block below that selects vdoStreamPath also selects playbackUrl and
// playbackStatus. resolveVideoSource (src/utils/video-source.js) depends on
// that habit, and scripts/check-video-fields.js is the build-time gate for it.
const REUSABLE_TOPIC_FIELDS = `
  id
  no
  nameLocal
  nameEng
  vdoLength
  vdoStreamPath
  playbackUrl
  playbackStatus
  videoProvider
  bunnyVideoGuid
  bunnyStatus
  fileSize
  vdoResolution
  vdoSet {
    id
    code
    subject { id code nameLocal nameEng }
  }
  vdoSetUnit { id unitSeq nameLocal nameEng }
  teacher {
    id code
    prefix { id nameShortLocal nameShortEng }
    firstNameLocal lastNameLocal firstNameEng lastNameEng
  }
`;

// The picker's own field list -- deliberately narrower than
// REUSABLE_TOPIC_FIELDS. The backend resolver does no `include`, so every
// relation below is a per-row round trip (src/resolvers/relation/**), and
// nameEng/vdoResolution/vdoSetUnit.nameEng/vdoSet.subject/teacher are not
// read anywhere the picker's rows flow: not the table columns, not
// BorrowVideoFilterBar's derived options, not PreviewVideoCard
// (resolveVideoSource reads only playbackStatus/playbackUrl), and not the
// parent onBorrowSelected handlers (VdoSetTopicEdit.vue /
// VdoSetTopicEditBunny.vue), which read only row.id. Dropping teacher alone
// removes the ~2x-rows-of-round-trips cost of Teacher.prefix, which has no
// short-circuit (src/resolvers/relation/profile/teacher.js).
//
// vdoStreamPath/playbackUrl/playbackStatus/videoProvider/bunnyVideoGuid/
// bunnyStatus stay together as one block -- scripts/check-video-fields.js
// scans for playbackUrl/playbackStatus alongside vdoStreamPath as a group.
const REUSABLE_TOPIC_PICKER_FIELDS = `
  id
  no
  nameLocal
  vdoLength
  vdoStreamPath
  playbackUrl
  playbackStatus
  videoProvider
  bunnyVideoGuid
  bunnyStatus
  fileSize
  vdoSet { id code }
  vdoSetUnit { id unitSeq nameLocal }
`;

export const GET_REUSABLE_VDOSETTOPICS = gql`
  query getReusableVdoSetTopics($filter: ReusableVdoSetTopicFilterInput) {
    getReusableVdoSetTopics(filter: $filter) { ${REUSABLE_TOPIC_PICKER_FIELDS} }
  }
`;

// Widened beyond REUSABLE_TOPIC_FIELDS (Finding 3): onEditClick reads
// isEnabled, remark, descriptionLocal, descriptionEng, thumbnail,
// courseOutline.id, vdoSetUnit.code/courseOutlineUnit and teacher.picturePath
// back into the form, and updateListAfterSave replaces both the list row and
// selectedVdoSetTopic wholesale with this mutation's result -- so a borrow
// response narrower than UPDATE_VDOSETTOPIC's blanked those fields out on the
// very next click into the topic. REUSABLE_TOPIC_FIELDS itself stays
// untouched: GET_REUSABLE_VDOSETTOPICS reuses it for the picker's rows, which
// run over the whole tenant and genuinely do not need these extra fields.
export const BORROW_VDOSETTOPIC_VIDEO = gql`
  mutation borrowVdoSetTopicVideo($targetVdoSetTopicId: ID!, $sourceVdoSetTopicId: ID!) {
    borrowVdoSetTopicVideo(
      targetVdoSetTopicId: $targetVdoSetTopicId
      sourceVdoSetTopicId: $sourceVdoSetTopicId
    ) {
      ${REUSABLE_TOPIC_FIELDS}
      bunnyStatusAt
      isEnabled
      remark
      descriptionLocal
      descriptionEng
      thumbnail
      courseOutline { id }
      vdoSet { id description totalVdoLength }
      vdoSetUnit { id code totalVdoLength courseOutlineUnit { id nameLocal nameEng } }
      teacher { id picturePath }
      sourceVdoSetTopic { ${REUSABLE_TOPIC_FIELDS} }
    }
  }
`;

// videoSharedWith runs one query per topic on the server, so it is asked for
// only here -- on a single topic, on the edit screen. Never add it to a list
// document (see the schema comment on VdoSetTopic.videoSharedWith).
export const GET_VDOSETTOPIC_SHARING = gql`
  query getVdoSetTopicSharing($id: ID!) {
    getVdoSetTopic(id: $id) {
      id
      vdoStreamPath
      playbackUrl
      playbackStatus
      videoProvider
      bunnyVideoGuid
      sourceVdoSetTopic {
        id
        no
        nameLocal
        nameEng
        vdoStreamPath
        playbackUrl
        playbackStatus
        videoProvider
        bunnyVideoGuid
        vdoSet { id code subject { id code nameLocal nameEng } }
        teacher { id code firstNameLocal lastNameLocal }
      }
      videoSharedWith {
        id
        no
        nameLocal
        nameEng
        vdoStreamPath
        playbackUrl
        playbackStatus
        videoProvider
        bunnyVideoGuid
        vdoSet { id code subject { id code nameLocal nameEng } }
        teacher { id code firstNameLocal lastNameLocal }
      }
    }
  }
`;
